/**
 * ChildOrgSection - Displays and manages child organizations
 *
 * For OrgAdmin users only. Shows list of child organizations with ability to create new ones.
 * On mobile, this section is read-only with a prompt to use web for creation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { isWeb as checkIsWeb } from '../../util/platform';
import { View, StyleSheet, Pressable, ActivityIndicator, TextInput, Modal } from 'react-native'
import { Feather } from '@expo/vector-icons';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';

import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import Util from '../../util/util';
import { useTranslation } from '../../i18n';

/**
 * @param {string} organization - Parent organization
 * @param {boolean} readOnly - If true, show read-only view (for mobile)
 * @param {function} onOrgChanged - Callback when orgs change (create, delete, update) to refresh parent
 */
const ChildOrgSection = ({ organization, readOnly = false, onOrgChanged }) => {
  const { t } = useTranslation(['admin', 'common']);

  const [childOrgs, setChildOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [accessMode, setAccessMode] = useState('inherit'); // 'inherit' or 'restricted'
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Edit access mode state
  const [editingOrg, setEditingOrg] = useState(null); // org being edited
  const [editAccessMode, setEditAccessMode] = useState('inherit');
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [showInheritConfirm, setShowInheritConfirm] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null); // org being deleted
  const [deleteInfo, setDeleteInfo] = useState(null); // pre-flight info from can-delete endpoint
  const [loadingDeleteFor, setLoadingDeleteFor] = useState(null); // tracks which org's delete is loading
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Report migration options state
  const [migrationStrategy, setMigrationStrategy] = useState('parent'); // 'parent', 'specific', or 'delete'
  const [targetOrganization, setTargetOrganization] = useState(null);

  const isWeb = checkIsWeb();

  const fetchChildOrgs = useCallback(async () => {
    if (!organization) return;

    setLoading(true);
    setError(null);

    try {
      const response = await Util.get('Resty', '/items/organization/children', {
        queryParams: { organization },
      });

      if (response?.items) {
        setChildOrgs(response.items);
      } else {
        setChildOrgs([]);
      }
    } catch (err) {
      console.error('Error fetching child organizations:', err);
      setError(t('childOrg.loadFailed'));
      setChildOrgs([]);
    } finally {
      setLoading(false);
    }
  }, [organization]);

  useEffect(() => {
    fetchChildOrgs();
  }, [fetchChildOrgs]);

  // Organization name validation regex (must match backend)
  // - 3-64 characters
  // - Alphanumeric with hyphens/underscores allowed (but not at start/end)
  const ORG_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_-]{1,62}[a-zA-Z0-9]$/;

  const validateOrgName = (name) => {
    const trimmed = name.trim();
    if (!trimmed) {
      return t('childOrg.nameRequired');
    }
    if (trimmed.length < 3) {
      return t('childOrg.nameTooShort');
    }
    if (trimmed.length > 64) {
      return t('childOrg.nameTooLong');
    }
    if (!ORG_NAME_REGEX.test(trimmed)) {
      return t('childOrg.nameInvalid');
    }
    return null;
  };

  const handleCreateOrg = async () => {
    const validationError = validateOrgName(newOrgName);
    if (validationError) {
      setCreateError(validationError);
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      const response = await Util.post('Resty', '/items/organization/child', {
        body: {
          parent_organization: organization,
          name: newOrgName.trim(),
          access_mode: accessMode,
        },
      });

      if (response?.error) {
        setCreateError(response.error);
        return;
      }

      // Refresh the list
      await fetchChildOrgs();

      // Reset form
      setNewOrgName('');
      setAccessMode('inherit');
      setShowCreateForm(false);

      // Notify parent component
      if (onOrgChanged) {
        onOrgChanged();
      }
    } catch (err) {
      console.error('Error creating child organization:', err);
      // Extract error message from API response if available
      let errorMessage = t('childOrg.createFailed');

      // Try various error structures
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.response?.body) {
        // Amplify v6 error structure - body might be parsed or raw
        try {
          const bodyData =
            typeof err.response.body === 'string' ? JSON.parse(err.response.body) : err.response.body;
          if (bodyData?.error) errorMessage = bodyData.error;
        } catch {
          // Body couldn't be parsed
        }
      } else if (err?.message) {
        // Check if message contains JSON with error (e.g., "409 {\"error\":\"...\"}")
        try {
          const match = err.message.match(/\{.*"error".*\}/);
          if (match) {
            const parsed = JSON.parse(match[0]);
            if (parsed?.error) errorMessage = parsed.error;
          } else {
            errorMessage = err.message;
          }
        } catch {
          errorMessage = err.message;
        }
      }

      setCreateError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleEditAccessMode = (org) => {
    setEditingOrg(org);
    setEditAccessMode(org.access_mode || 'inherit');
    setUpdateError(null);
  };

  const handleCloseEditModal = () => {
    setEditingOrg(null);
    setEditAccessMode('inherit');
    setUpdateError(null);
    setShowInheritConfirm(false);
  };

  const handleAccessModeChange = (newMode) => {
    // If switching to inherit, show confirmation first
    if (newMode === 'inherit' && editingOrg?.access_mode !== 'inherit') {
      setEditAccessMode(newMode);
      setShowInheritConfirm(true);
    } else {
      setEditAccessMode(newMode);
      setShowInheritConfirm(false);
    }
  };

  const handleSaveAccessMode = async () => {
    if (!editingOrg) return;

    setUpdating(true);
    setUpdateError(null);

    try {
      const response = await Util.patch('Resty', '/items/organization/access-mode', {
        body: {
          organization: editingOrg.organization,
          access_mode: editAccessMode,
        },
      });

      if (response?.error) {
        setUpdateError(response.error);
        return;
      }

      // Refresh the list to show updated access mode
      await fetchChildOrgs();
      handleCloseEditModal();

      // Notify parent in case routing needs refresh
      if (onOrgChanged) {
        onOrgChanged();
      }
    } catch (err) {
      console.error('Error updating access mode:', err);
      setUpdateError(err.message || t('childOrg.updateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClick = async (org) => {
    setLoadingDeleteFor(org.organization);
    setDeleteTarget(org);
    setDeleteInfo(null);
    setDeleteError(null);

    try {
      const response = await Util.get('Resty', '/items/organization/child/can-delete', {
        queryParams: { organization: org.organization },
      });

      if (response?.error) {
        setDeleteError(response.error);
        setDeleteInfo(null);
      } else {
        setDeleteInfo(response);
      }
    } catch (err) {
      console.error('Error checking delete eligibility:', err);
      setDeleteError(err.message || t('deleteOrg.loadFailed'));
    } finally {
      setLoadingDeleteFor(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteInfo(null);
    setDeleteError(null);
    setLoadingDeleteFor(null);
    setDeleting(false);
    setMigrationStrategy('parent');
    setTargetOrganization(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !deleteInfo?.can_delete) return;

    // Validate target org is selected when migration strategy is 'specific'
    if (migrationStrategy === 'specific' && !targetOrganization) {
      setDeleteError(t('deleteOrg.selectTarget'));
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    try {
      const queryParams = {
        organization: deleteTarget.organization,
        migration_strategy: migrationStrategy,
      };
      if (migrationStrategy === 'specific' && targetOrganization) {
        queryParams.target_organization = targetOrganization;
      }

      const response = await Util.del('Resty', '/items/organization/child', {
        queryParams,
      });

      if (response?.error) {
        setDeleteError(response.error);
        return;
      }

      // Refresh the list
      await fetchChildOrgs();
      handleCloseDeleteModal();

      // Notify parent component to refresh routing
      if (onOrgChanged) {
        onOrgChanged();
      }
    } catch (err) {
      console.error('Error deleting child organization:', err);
      setDeleteError(err.message || t('deleteOrg.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
          <CustomText style={styles.loadingText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('childOrg.loading')}
          </CustomText>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Feather name="alert-circle" size={20} color={AppStyles.colorSet.error} />
          <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.error}>
            {error}
          </CustomText>
          <Pressable onPress={fetchChildOrgs} style={styles.retryButton}>
            <CustomText size={13} color={AppStyles.colorSet.brandPrimary}>
              {t('common:actions.retry')}
            </CustomText>
          </Pressable>
        </View>
      </View>
    );
  }

  const canCreateOrgs = !readOnly && isWeb;

  return (
    <View style={styles.container}>
      <CustomText
        style={styles.heading}
        font="semiBold"
        size={18}
        color={AppStyles.colorSet.textPrimary}
      >
        {t('childOrg.title')}
      </CustomText>
      <CustomText
        style={styles.explanationText}
        font="regular"
        size={14}
        color={AppStyles.colorSet.textSecondary}
      >
        {t('childOrg.description')}
      </CustomText>

      {childOrgs.length === 0 ? (
        <View style={styles.emptyState}>
          <Feather name="git-branch" size={32} color={AppStyles.colorSet.textMuted} />
          <CustomText style={styles.emptyText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('childOrg.empty')}
          </CustomText>
          {readOnly && !isWeb && (
            <CustomText style={styles.emptySubtext} size={13} color={AppStyles.colorSet.textMuted}>
              {t('childOrg.webOnlyHint')}
            </CustomText>
          )}
        </View>
      ) : (
        <View style={styles.orgList}>
          {childOrgs.map((org, index) => (
            <View key={org.organization || index} style={styles.orgItem}>
              <Feather name="folder" size={18} color={AppStyles.colorSet.textSecondary} />
              <View style={styles.orgInfoContainer}>
                <View style={styles.orgNameRow}>
                  <CustomText style={styles.orgName} size={14} color={AppStyles.colorSet.textPrimary}>
                    {org.organization}
                  </CustomText>
                  <View
                    style={[
                      styles.accessModeBadge,
                      (org.access_mode || 'inherit') === 'inherit'
                        ? styles.accessModeBadgeInherit
                        : styles.accessModeBadgeRestricted,
                    ]}
                  >
                    <Feather
                      name={(org.access_mode || 'inherit') === 'inherit' ? 'users' : 'lock'}
                      size={10}
                      color={
                        (org.access_mode || 'inherit') === 'inherit'
                          ? '#B45309' // Darker amber for contrast on light yellow bg
                          : AppStyles.colorSet.textSecondary
                      }
                    />
                    <CustomText
                      size={11}
                      color={
                        (org.access_mode || 'inherit') === 'inherit'
                          ? '#B45309' // Darker amber for contrast on light yellow bg
                          : AppStyles.colorSet.textSecondary
                      }
                    >
                      {(org.access_mode || 'inherit') === 'inherit' ? t('childOrg.accessInherit') : t('childOrg.accessRestricted')}
                    </CustomText>
                  </View>
                </View>
                {org.created_at && (
                  <CustomText
                    style={styles.orgDate}
                    size={12}
                    color={AppStyles.colorSet.textMuted}
                  >
                    {t('childOrg.createdOn', { date: new Date(org.created_at).toLocaleDateString() })}
                  </CustomText>
                )}
              </View>
              {canCreateOrgs && (
                <View style={styles.orgActions}>
                  <Pressable
                    style={({ hovered, focused }) => [
                      styles.editButton,
                      { cursor: 'pointer' },
                      (hovered || focused) && styles.iconButtonHovered,
                    ]}
                    onPress={() => handleEditAccessMode(org)}
                    accessibilityLabel={t('childOrg.editAccessModeLabel', { organization: org.organization })}
                    accessibilityRole="button"
                  >
                    <Feather name="settings" size={16} color={AppStyles.colorSet.textSecondary} />
                  </Pressable>
                  <Pressable
                    style={({ hovered, focused }) => [
                      styles.deleteButton,
                      { cursor: 'pointer' },
                      (hovered || focused) && styles.iconButtonHovered,
                    ]}
                    onPress={() => handleDeleteClick(org)}
                    accessibilityLabel={`Delete ${org.organization}`}
                    accessibilityRole="button"
                    disabled={loadingDeleteFor === org.organization}
                  >
                    {loadingDeleteFor === org.organization ? (
                      <ActivityIndicator size="small" color={AppStyles.colorSet.error} />
                    ) : (
                      <Feather name="trash-2" size={16} color={AppStyles.colorSet.error} />
                    )}
                  </Pressable>
                </View>
              )}
            </View>
          ))}
        </View>
      )}

      {readOnly && !isWeb ? (
        <View style={styles.webOnlyNotice}>
          <Feather name="monitor" size={16} color={AppStyles.colorSet.textMuted} />
          <CustomText style={styles.webOnlyText} size={13} color={AppStyles.colorSet.textMuted}>
            {t('childOrg.webOnlyHint')}
          </CustomText>
        </View>
      ) : canCreateOrgs ? (
        <View style={styles.createSection}>
          {!showCreateForm ? (
            <Pressable
              style={({ pressed, hovered, focused }) => [
                styles.createButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.createButtonHovered,
                pressed && styles.createButtonPressed,
              ]}
              onPress={() => setShowCreateForm(true)}
              accessibilityLabel={t('childOrg.createButton')}
              accessibilityRole="button"
            >
              <Feather name="plus" size={18} color={AppStyles.colorSet.textPrimary} />
              <CustomText
                style={styles.createButtonText}
                size={14}
                font="medium"
                color={AppStyles.colorSet.textPrimary}
              >
                {t('childOrg.createButton')}
              </CustomText>
            </Pressable>
          ) : (
            <View style={styles.createFormContainer}>
              <CustomText
                style={styles.createFormTitle}
                font="medium"
                size={15}
                color={AppStyles.colorSet.textPrimary}
              >
                {t('childOrg.createTitle')}
              </CustomText>

              <View style={styles.inputLabelRow}>
                <CustomText
                  style={styles.inputLabel}
                  size={13}
                  color={AppStyles.colorSet.textSecondary}
                >
                  {t('childOrg.nameLabel')}
                </CustomText>
                <CustomText
                  size={12}
                  color={newOrgName.length > 64 ? AppStyles.colorSet.error : AppStyles.colorSet.textMuted}
                >
                  {newOrgName.length}/64
                </CustomText>
              </View>
              <TextInput
                style={[
                  styles.input,
                  createError && styles.inputError,
                ]}
                value={newOrgName}
                onChangeText={(text) => {
                  setNewOrgName(text);
                  // Real-time validation (only show errors after user starts typing)
                  if (text.trim().length > 0) {
                    setCreateError(validateOrgName(text));
                  } else {
                    setCreateError(null);
                  }
                }}
                placeholder={t('childOrg.namePlaceholder')}
                placeholderTextColor={AppStyles.colorSet.textMuted}
                autoFocus
                editable={!creating}
                maxLength={65}
              />

              {createError && (
                <View style={styles.createErrorContainer}>
                  <Feather name="alert-circle" size={14} color={AppStyles.colorSet.error} />
                  <CustomText
                    style={styles.createErrorText}
                    size={13}
                    color={AppStyles.colorSet.error}
                  >
                    {createError}
                  </CustomText>
                </View>
              )}

              {/* Access Mode Selector */}
              <CustomText
                style={[styles.inputLabel, { marginTop: 16 }]}
                size={13}
                color={AppStyles.colorSet.textSecondary}
              >
                {t('childOrg.accessModeLabel')}
              </CustomText>
              <View style={styles.accessModeContainer}>
                <Pressable
                  style={({ hovered, focused }) => [
                    styles.accessModeOption,
                    { cursor: 'pointer' },
                    accessMode !== 'inherit' && (hovered || focused) && styles.accessModeOptionHovered,
                    accessMode === 'inherit' && styles.accessModeOptionSelected,
                  ]}
                  onPress={() => setAccessMode('inherit')}
                  disabled={creating}
                  accessibilityLabel={t('childOrg.inheritFromParent')}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: accessMode === 'inherit' }}
                >
                  <View style={styles.accessModeRadio}>
                    {accessMode === 'inherit' && <View style={styles.accessModeRadioInner} />}
                  </View>
                  <View style={styles.accessModeTextContainer}>
                    <CustomText
                      size={14}
                      font="medium"
                      color={AppStyles.colorSet.textPrimary}
                    >
                      {t('childOrg.inheritFromParent')}
                    </CustomText>
                    <CustomText
                      size={12}
                      color={AppStyles.colorSet.textMuted}
                    >
                      {t('childOrg.inheritDescription')}
                    </CustomText>
                  </View>
                </Pressable>

                <Pressable
                  style={({ hovered, focused }) => [
                    styles.accessModeOption,
                    { cursor: 'pointer' },
                    accessMode !== 'restricted' && (hovered || focused) && styles.accessModeOptionHovered,
                    accessMode === 'restricted' && styles.accessModeOptionSelected,
                  ]}
                  onPress={() => setAccessMode('restricted')}
                  disabled={creating}
                  accessibilityLabel={t('childOrg.accessRestricted')}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: accessMode === 'restricted' }}
                >
                  <View style={styles.accessModeRadio}>
                    {accessMode === 'restricted' && <View style={styles.accessModeRadioInner} />}
                  </View>
                  <View style={styles.accessModeTextContainer}>
                    <CustomText
                      size={14}
                      font="medium"
                      color={AppStyles.colorSet.textPrimary}
                    >
                      {t('childOrg.accessRestricted')}
                    </CustomText>
                    <CustomText
                      size={12}
                      color={AppStyles.colorSet.textMuted}
                    >
                      {t('childOrg.restrictedDescription')}
                    </CustomText>
                  </View>
                </Pressable>
              </View>

              <CustomText
                style={styles.createHint}
                size={12}
                color={AppStyles.colorSet.textMuted}
              >
                {t('childOrg.inheritHint', { organization })}
              </CustomText>

              <View style={styles.createFormButtons}>
                <Pressable
                  style={styles.cancelButton}
                  onPress={() => {
                    setShowCreateForm(false);
                    setNewOrgName('');
                    setAccessMode('inherit');
                    setCreateError(null);
                  }}
                  disabled={creating}
                  accessibilityLabel={t('common:actions.cancel')}
                  accessibilityRole="button"
                >
                  <CustomText size={14} color={AppStyles.colorSet.textSecondary}>
                    {t('common:actions.cancel')}
                  </CustomText>
                </Pressable>

                <Pressable
                  style={({ pressed }) => [
                    styles.saveButton,
                    !newOrgName.trim() && styles.saveButtonDisabled,
                    pressed && styles.saveButtonPressed,
                  ]}
                  onPress={handleCreateOrg}
                  disabled={!newOrgName.trim() || creating}
                  accessibilityLabel={t('childOrg.create')}
                  accessibilityRole="button"
                >
                  {creating ? (
                    <ActivityIndicator size="small" color={AppStyles.colorSet.textPrimary} />
                  ) : (
                    <>
                      <Feather name="check" size={16} color={AppStyles.colorSet.textPrimary} />
                      <CustomText
                        style={styles.saveButtonText}
                        size={14}
                        font="medium"
                        color={AppStyles.colorSet.textPrimary}
                      >
                        {t('childOrg.create')}
                      </CustomText>
                    </>
                  )}
                </Pressable>
              </View>
            </View>
          )}
        </View>
      ) : null}

      {/* Edit Access Mode Modal */}
      <Modal
        visible={!!editingOrg}
        transparent
        animationType="fade"
        onRequestClose={handleCloseEditModal}
      >
        <Pressable style={styles.modalOverlay} onPress={handleCloseEditModal}>
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            <View style={styles.modalHeader}>
              <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
                {t('childOrg.editAccessTitle')}
              </CustomText>
              <Pressable onPress={handleCloseEditModal} style={styles.modalCloseButton}>
                <Feather name="x" size={20} color={AppStyles.colorSet.textSecondary} />
              </Pressable>
            </View>

            <CustomText
              style={styles.modalSubtitle}
              size={14}
              color={AppStyles.colorSet.textSecondary}
            >
              {editingOrg?.organization}
            </CustomText>

            {/* Access Mode Options */}
            <View style={styles.modalAccessModeContainer}>
              <Pressable
                style={[
                  styles.accessModeOption,
                  editAccessMode === 'inherit' && styles.accessModeOptionSelected,
                ]}
                onPress={() => handleAccessModeChange('inherit')}
                disabled={updating}
              >
                <View style={styles.accessModeRadio}>
                  {editAccessMode === 'inherit' && <View style={styles.accessModeRadioInner} />}
                </View>
                <View style={styles.accessModeTextContainer}>
                  <CustomText size={14} font="medium" color={AppStyles.colorSet.textPrimary}>
                    {t('childOrg.inheritFromParent')}
                  </CustomText>
                  <CustomText size={12} color={AppStyles.colorSet.textMuted}>
                    {t('childOrg.inheritDescription')}
                  </CustomText>
                </View>
              </Pressable>

              <Pressable
                style={[
                  styles.accessModeOption,
                  editAccessMode === 'restricted' && styles.accessModeOptionSelected,
                ]}
                onPress={() => handleAccessModeChange('restricted')}
                disabled={updating}
              >
                <View style={styles.accessModeRadio}>
                  {editAccessMode === 'restricted' && <View style={styles.accessModeRadioInner} />}
                </View>
                <View style={styles.accessModeTextContainer}>
                  <CustomText size={14} font="medium" color={AppStyles.colorSet.textPrimary}>
                    {t('childOrg.accessRestricted')}
                  </CustomText>
                  <CustomText size={12} color={AppStyles.colorSet.textMuted}>
                    {t('childOrg.restrictedDescriptionEdit')}
                  </CustomText>
                </View>
              </Pressable>
            </View>

            {/* Inherit confirmation warning */}
            {showInheritConfirm && (
              <View style={styles.inheritWarning}>
                <Feather name="alert-triangle" size={16} color="#B45309" />
                <CustomText style={styles.inheritWarningText} size={13} color="#B45309">
                  {t('childOrg.inheritConfirmWarning', { organization })}
                </CustomText>
              </View>
            )}

            {updateError && (
              <View style={styles.createErrorContainer}>
                <Feather name="alert-circle" size={14} color={AppStyles.colorSet.error} />
                <CustomText
                  style={styles.createErrorText}
                  size={13}
                  color={AppStyles.colorSet.error}
                >
                  {updateError}
                </CustomText>
              </View>
            )}

            <View style={styles.modalButtons}>
              <Pressable
                style={styles.cancelButton}
                onPress={handleCloseEditModal}
                disabled={updating}
              >
                <CustomText size={14} color={AppStyles.colorSet.textSecondary}>
                  {t('common:actions.cancel')}
                </CustomText>
              </Pressable>

              <Pressable
                style={({ pressed }) => [
                  styles.saveButton,
                  editAccessMode === (editingOrg?.access_mode || 'inherit') && styles.saveButtonDisabled,
                  pressed && styles.saveButtonPressed,
                ]}
                onPress={handleSaveAccessMode}
                disabled={updating || editAccessMode === (editingOrg?.access_mode || 'inherit')}
              >
                {updating ? (
                  <ActivityIndicator size="small" color={AppStyles.colorSet.textPrimary} />
                ) : (
                  <>
                    <Feather name="check" size={16} color={AppStyles.colorSet.textPrimary} />
                    <CustomText
                      style={styles.saveButtonText}
                      size={14}
                      font="medium"
                      color={AppStyles.colorSet.textPrimary}
                    >
                      {t('common:actions.save')}
                    </CustomText>
                  </>
                )}
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Delete Organization Modal */}
      <Modal
        visible={!!deleteTarget}
        transparent
        animationType="fade"
        onRequestClose={handleCloseDeleteModal}
      >
        <Pressable style={styles.modalOverlay} onPress={handleCloseDeleteModal}>
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
                {t(deleteInfo?.can_delete ? 'deleteOrg.title' : 'deleteOrg.blockedTitle')}
              </CustomText>
              <Pressable onPress={handleCloseDeleteModal} style={styles.modalCloseButton}>
                <Feather name="x" size={20} color={AppStyles.colorSet.textSecondary} />
              </Pressable>
            </View>

            <CustomText
              style={styles.modalSubtitle}
              size={14}
              color={AppStyles.colorSet.textSecondary}
            >
              {deleteTarget?.organization}
            </CustomText>

            {/* Loading state */}
            {loadingDeleteFor !== null && (
              <View style={styles.deleteLoadingContainer}>
                <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
                <CustomText style={styles.loadingText} size={14} color={AppStyles.colorSet.textSecondary}>
                  {t('deleteOrg.checking')}
                </CustomText>
              </View>
            )}

            {/* Error state */}
            {deleteError && loadingDeleteFor === null && (
              <View style={styles.deleteErrorBox}>
                <Feather name="alert-circle" size={16} color="#DC2626" />
                <CustomText style={styles.deleteErrorBoxText} size={13} color="#DC2626">
                  {deleteError}
                </CustomText>
              </View>
            )}

            {/* Blocked: Has child organizations */}
            {loadingDeleteFor === null && deleteInfo && deleteInfo.has_children > 0 && (
              <View style={styles.deleteBlockedContainer}>
                <View style={styles.deleteBlockedWarning}>
                  <Feather name="alert-triangle" size={20} color="#B45309" />
                  <View style={styles.deleteBlockedTextContainer}>
                    <CustomText size={14} font="medium" color="#B45309">
                      {t('deleteOrg.hasChildren', { count: deleteInfo.has_children })}
                    </CustomText>
                    <CustomText style={styles.deleteBlockedSubtext} size={13} color="#92400E">
                      {t('deleteOrg.hasChildrenHint')}
                    </CustomText>
                  </View>
                </View>
              </View>
            )}

            {/* Blocked: Users have this as default org */}
            {loadingDeleteFor === null && deleteInfo && deleteInfo.has_children === 0 && deleteInfo.users_with_default > 0 && (
              <View style={styles.deleteBlockedContainer}>
                <View style={styles.deleteBlockedWarning}>
                  <Feather name="alert-triangle" size={20} color="#B45309" />
                  <View style={styles.deleteBlockedTextContainer}>
                    <CustomText size={14} font="medium" color="#B45309">
                      {t('deleteOrg.usersWithDefault', { count: deleteInfo.users_with_default })}
                    </CustomText>
                    <CustomText style={styles.deleteBlockedSubtext} size={13} color="#92400E">
                      {t('childOrg.toDeleteThisOrg')}
                    </CustomText>
                  </View>
                </View>
                <View style={styles.deleteBlockedSteps}>
                  <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                    {t('deleteOrg.step1')}
                  </CustomText>
                  <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                    {t('deleteOrg.step2')}
                  </CustomText>
                  <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                    {t('deleteOrg.step3')}
                  </CustomText>
                  <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                    {t('deleteOrg.step4')}
                  </CustomText>
                </View>
              </View>
            )}

            {/* Allowed: Can delete */}
            {loadingDeleteFor === null && deleteInfo?.can_delete && (
              <View style={styles.deleteConfirmContainer}>
                <CustomText size={14} color={AppStyles.colorSet.textSecondary}>
                  {t('childOrg.confirmDelete')}
                </CustomText>

                {/* Report migration options */}
                {deleteInfo.reports_to_migrate > 0 && (
                  <View style={styles.migrationOptionsContainer}>
                    <CustomText size={13} font="medium" color={AppStyles.colorSet.textPrimary}>
                      {t('deleteOrg.reportsInOrg', { count: deleteInfo.reports_to_migrate })}
                    </CustomText>

                    {/* Option: Migrate to parent */}
                    <Pressable
                      style={styles.migrationOption}
                      onPress={() => setMigrationStrategy('parent')}
                    >
                      <View style={[styles.radioCircle, migrationStrategy === 'parent' && styles.radioCircleSelected]}>
                        {migrationStrategy === 'parent' && <View style={styles.radioInner} />}
                      </View>
                      <View style={styles.migrationOptionText}>
                        <CustomText size={13} color={AppStyles.colorSet.textPrimary}>
                          {t('deleteOrg.moveToParent', { organization: deleteInfo.parent_organization })}
                        </CustomText>
                        <CustomText size={12} color={AppStyles.colorSet.textSecondary}>
                          {t('deleteOrg.recommended')}
                        </CustomText>
                      </View>
                    </Pressable>

                    {/* Option: Migrate to specific org */}
                    {deleteInfo.available_migration_targets?.length > 1 && (
                      <>
                        <Pressable
                          style={styles.migrationOption}
                          onPress={() => setMigrationStrategy('specific')}
                        >
                          <View style={[styles.radioCircle, migrationStrategy === 'specific' && styles.radioCircleSelected]}>
                            {migrationStrategy === 'specific' && <View style={styles.radioInner} />}
                          </View>
                          <CustomText size={13} color={AppStyles.colorSet.textPrimary}>
                            {t('deleteOrg.moveToOther')}
                          </CustomText>
                        </Pressable>

                        {migrationStrategy === 'specific' && (
                          <View style={styles.targetOrgSelector}>
                            {deleteInfo.available_migration_targets
                              .filter((org) => org !== deleteInfo.parent_organization)
                              .map((org) => (
                                <Pressable
                                  key={org}
                                  style={[
                                    styles.targetOrgOption,
                                    targetOrganization === org && styles.targetOrgOptionSelected,
                                  ]}
                                  onPress={() => setTargetOrganization(org)}
                                >
                                  <CustomText
                                    size={12}
                                    color={targetOrganization === org ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                                  >
                                    {org}
                                  </CustomText>
                                </Pressable>
                              ))}
                          </View>
                        )}
                      </>
                    )}

                    {/* Option: Delete reports */}
                    <Pressable
                      style={styles.migrationOption}
                      onPress={() => setMigrationStrategy('delete')}
                    >
                      <View style={[styles.radioCircle, migrationStrategy === 'delete' && styles.radioCircleSelectedDanger]}>
                        {migrationStrategy === 'delete' && <View style={styles.radioInnerDanger} />}
                      </View>
                      <CustomText size={13} color="#DC2626">
                        {t('deleteOrg.deleteReports')}
                      </CustomText>
                    </Pressable>
                  </View>
                )}

                {/* Other impacts */}
                {(deleteInfo.routing_rules_affected > 0 || deleteInfo.users_with_access > 0 || deleteInfo.subscriptions_affected > 0) && (
                  <View style={styles.deleteImpactList}>
                    <CustomText size={13} font="medium" color={AppStyles.colorSet.textPrimary}>
                      {t('deleteOrg.alsoTitle')}
                    </CustomText>
                    {deleteInfo.routing_rules_affected > 0 && (
                      <View style={styles.deleteImpactItem}>
                        <Feather name="git-merge" size={14} color={AppStyles.colorSet.textSecondary} />
                        <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                          {t('deleteOrg.removeRoutingRules', { count: deleteInfo.routing_rules_affected })}
                        </CustomText>
                      </View>
                    )}
                    {deleteInfo.users_with_access > 0 && (
                      <View style={styles.deleteImpactItem}>
                        <Feather name="users" size={14} color={AppStyles.colorSet.textSecondary} />
                        <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                          {t('deleteOrg.revokeAccess', { count: deleteInfo.users_with_access })}
                        </CustomText>
                      </View>
                    )}
                    {deleteInfo.subscriptions_affected > 0 && (
                      <View style={styles.deleteImpactItem}>
                        <Feather name="bell" size={14} color={AppStyles.colorSet.textSecondary} />
                        <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                          {t('deleteOrg.deleteSubscriptions', { count: deleteInfo.subscriptions_affected })}
                        </CustomText>
                      </View>
                    )}
                  </View>
                )}

                <View style={styles.deleteWarningNote}>
                  <Feather name="alert-circle" size={14} color="#DC2626" />
                  <CustomText size={12} color="#DC2626">
                    {t('deleteOrg.cannotBeUndone')}
                  </CustomText>
                </View>
              </View>
            )}

            {/* Modal Buttons */}
            <View style={styles.modalButtons}>
              <Pressable
                style={styles.cancelButton}
                onPress={handleCloseDeleteModal}
                disabled={deleting}
              >
                <CustomText size={14} color={AppStyles.colorSet.textSecondary}>
                  {deleteInfo?.can_delete ? 'Cancel' : 'Close'}
                </CustomText>
              </Pressable>

              {deleteInfo?.can_delete === true && (
                <Pressable
                  style={styles.deleteConfirmButton}
                  onPress={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <>
                      <Feather name="trash-2" size={16} color="#FFFFFF" />
                      <CustomText
                        style={styles.deleteConfirmButtonText}
                        size={14}
                        font="medium"
                        color="#FFFFFF"
                      >
                        {t('deleteOrg.delete')}
                      </CustomText>
                    </>
                  )}
                </Pressable>
              )}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 12,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: hp(3),
  },
  loadingText: {
    marginLeft: 10,
  },
  errorContainer: {
    alignItems: 'center',
    paddingVertical: hp(2),
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    padding: 16,
  },
  errorText: {
    marginTop: 8,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    padding: 8,
  },
  heading: {
    marginTop: hp(1),
    marginBottom: hp(0.5),
  },
  explanationText: {
    marginBottom: hp(1.5),
    lineHeight: 20,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: hp(3),
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderStyle: 'dashed',
  },
  emptyText: {
    marginTop: 8,
  },
  emptySubtext: {
    marginTop: 4,
    textAlign: 'center',
  },
  orgList: {
    marginBottom: hp(1),
  },
  orgItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  orgInfoContainer: {
    flex: 1,
    marginLeft: 12,
  },
  orgNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  orgName: {},
  orgDate: {
    marginTop: 2,
  },
  accessModeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    gap: 4,
  },
  accessModeBadgeInherit: {
    backgroundColor: '#FFFBEB',
    borderWidth: 1,
    borderColor: '#FCD34D',
  },
  accessModeBadgeRestricted: {
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  editButton: {
    padding: 8,
    marginLeft: 8,
  },
  // Web hover/focus feedback for the settings/delete icon buttons
  iconButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
    borderRadius: 8,
  },
  createSection: {
    marginTop: hp(1),
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 14,
    borderRadius: 12,
  },
  createButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  createButtonPressed: {
    opacity: 0.8,
  },
  createButtonText: {
    marginLeft: 8,
  },
  createFormContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  createFormTitle: {
    marginBottom: 16,
  },
  inputLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  inputLabel: {},
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
  },
  inputError: {
    borderColor: AppStyles.colorSet.error,
  },
  createErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  createErrorText: {
    marginLeft: 6,
  },
  createHint: {
    marginTop: 8,
    fontStyle: 'italic',
  },
  createFormButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    gap: 12,
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  saveButtonDisabled: {
    backgroundColor: AppStyles.colorSet.textMuted,
    opacity: 0.5,
  },
  saveButtonPressed: {
    opacity: 0.8,
  },
  saveButtonText: {
    marginLeft: 6,
  },
  webOnlyNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    marginTop: hp(1),
  },
  webOnlyText: {
    marginLeft: 8,
  },
  accessModeContainer: {
    gap: 8,
  },
  accessModeOption: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderRadius: 8,
  },
  accessModeOptionSelected: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: '#FFFBEB',
  },
  accessModeOptionHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: AppStyles.stateSet.hover,
  },
  accessModeRadio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: AppStyles.colorSet.textMuted,
    marginRight: 12,
    marginTop: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  accessModeRadioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  accessModeTextContainer: {
    flex: 1,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    width: '100%',
    maxWidth: 400,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  modalCloseButton: {
    padding: 4,
  },
  modalSubtitle: {
    marginBottom: 16,
  },
  modalAccessModeContainer: {
    gap: 8,
    marginBottom: 16,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
    gap: 12,
  },
  inheritWarning: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFBEB',
    borderWidth: 1,
    borderColor: '#FCD34D',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    gap: 8,
  },
  inheritWarningText: {
    flex: 1,
    lineHeight: 18,
  },
  // Organization actions (edit + delete buttons)
  orgActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButton: {
    padding: 8,
    marginLeft: 4,
  },
  // Delete modal styles
  deleteLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  deleteErrorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FEF2F2',
    borderWidth: 1,
    borderColor: '#FECACA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    gap: 8,
  },
  deleteErrorBoxText: {
    flex: 1,
    lineHeight: 18,
  },
  deleteBlockedContainer: {
    marginBottom: 16,
  },
  deleteBlockedWarning: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFBEB',
    borderWidth: 1,
    borderColor: '#FCD34D',
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  deleteBlockedTextContainer: {
    flex: 1,
  },
  deleteBlockedSubtext: {
    marginTop: 4,
    lineHeight: 18,
  },
  deleteBlockedSteps: {
    marginTop: 12,
    paddingLeft: 8,
    gap: 4,
  },
  deleteConfirmContainer: {
    marginBottom: 8,
  },
  deleteImpactList: {
    marginTop: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    gap: 8,
  },
  deleteImpactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 4,
  },
  deleteWarningNote: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    gap: 6,
  },
  deleteConfirmButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DC2626',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  deleteConfirmButtonPressed: {
    opacity: 0.8,
  },
  deleteConfirmButtonText: {
    marginLeft: 6,
  },
  // Migration options styles
  migrationOptionsContainer: {
    marginTop: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  migrationOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 6,
  },
  migrationOptionText: {
    flex: 1,
  },
  radioCircle: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioCircleSelected: {
    borderColor: AppStyles.colorSet.brandPrimary,
  },
  radioCircleSelectedDanger: {
    borderColor: '#DC2626',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  radioInnerDanger: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#DC2626',
  },
  targetOrgSelector: {
    marginLeft: 28,
    marginTop: 4,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  targetOrgOption: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  targetOrgOptionSelected: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: '#FFF8E7',
  },
});

export default ChildOrgSection;
